import type { ApiResponse } from '@shared/types'
import type {
  SmsGeoPermissionsPayload,
  SmsGeoPermissionsResponse,
} from 'v2/api-console/messaging/geo-permissions/types'
import type {
  MessageLog,
  RMessageDebugLogs,
  RMessageLogs,
} from 'v2/api-console/messaging/message-logs/types'
import { getAgentTimezone } from 'v2/api-console/shared/utils/date-utils'
import {
  dateRangeToUtc,
  durationRangeToBounds,
  sanitizePhoneParam,
  toCdrPhoneNumber,
} from 'v2/api-console/shared/utils/recording-filter-utils'
import type {
  RAudioStats,
  RCallDebugLogs,
  RCallInsights,
  RCallLogs,
  RCallRawLogs,
  RCallRecordings,
  RCallTranscriptions,
} from 'v2/api-console/voice/call-logs/types'
import type {
  RGeoPermissions,
  RGeoPermissionsPayload,
} from 'v2/api-console/voice/geo-permissions/types'
import { buildTranscriptionListParams } from 'v2/api-console/voice/transcriptions/transcription-filter-params'
import type { RVoiceAlertPayload, RVoiceAlerts } from 'v2/api-console/voice/voice-alerts/types'
import type {
  RSipTrunkingGeoPermissions,
  RSipTrunkingGeoPermissionsPayload,
} from 'v2/api-console/zentrunk/geo-permissions/types'
import type { RZentrunkLogs, ZentrunkCDRDetail } from 'v2/api-console/zentrunk/logs/types'
import { BackendServiceName, type GetService, type PostService } from 'v2/types'
import type { RUsageMonthlyTotal, RUsageSummary } from 'v2/types/api/billing'
import type {
  PortInLoaRequest,
  RPortInExtractionResponse,
  RPortInGeneratedLoa,
} from 'v2/types/api/port-in'
import dayjs from 'v2/utils/dayjs'
import { baseService, coreService } from './base-services'
import { createService } from './create-service'

const API_CONSOLE_ENDPOINTS = {
  // Voice Geo Permissions
  listGeoPermissions: '/geopermissions',
  saveGeoPermission: '/geopermissions/save',
  exportGeoPermissions: '/geopermissions/export',
  removeOverride: '/geopermissions/remove',

  // SIP Trunking Geo Permissions (SER-6024, account-level / default trunk only)
  sipTrunkingGeoPermissions: '/geopermissions/sip-trunking',
  exportSipTrunkingPremiumPrefixes: '/geopermissions/sip-trunking/premium-prefixes/export',

  // SMS Geo Permissions
  listSmsGeoPermissions: '/sms/settings/geo-permissions',
  saveSmsGeoPermissions: '/sms/settings/geo-permissions',
  removeSmsGeoPermissionsOverride: '/sms/settings/geo-permissions',

  // Call logs
  listCallLogs: '/plivo-api/Call/',
  listSubaccountCallLogs: (subaccountId: string) => `/calls/subaccount/${subaccountId}/logs`,
  callInsights: (callUuid: string) => `/calls/${callUuid}/insights`,
  audioStats: (callUuid: string) => `/calls/${callUuid}/audio-stats`,
  callDebugLogs: (callUuid: string) => `/calls/${callUuid}/debug_logs`,
  callRawLogs: (callUuid: string) => `/calls/${callUuid}/raw_logs`,
  // Recordings
  listCallRecordings: `/plivo-api/Recording/`,
  listSubaccountRecordings: (subaccountId: string) => `/recordings/subaccount/${subaccountId}/logs`,
  deleteRecording: (recordingId: string) =>
    `/plivo-api/Recording/${encodeURIComponent(recordingId)}/`,
  exportRecordings: '/logs/recordings/export',
  bulkDeleteRecordings: '/logs/recordings/bulk_delete',
  // Transcription
  listCallTranscriptions: `/plivo-api/Transcription/`,
  // Message logs
  listMessageLogs: '/plivo-api/Message/',
  // Usage summary (proxied to api.plivo.com/v1/Account/{authId}/UsageSummary/)
  usageSummary: '/plivo-api/UsageSummary/',
  // Month-to-date spend only, via the hodor plivo-analytics proxy straight to
  // messaging-dashboard. Cheaper than usageSummary: that endpoint derives its total from a
  // full-window aggregate that times out on accounts with many subaccounts.
  usageMonthlyTotal: '/plivo-analytics/usage-monthly-total',
  messageInsights: (messageUuid: string) => `/messages/${messageUuid}/insights`,
  messageDebugLogs: (messageUuid: string) => `/messages/${messageUuid}/debug_logs`,
  exportMessageLogs: '/logs/messages/export',
  // Call logs export
  exportCallLogs: '/logs/calls/export',
  exportConcurrencyData: '/logs/calls/export/concurrency',
  // Zentrunk Logs
  listZentrunkLogs: '/zentrunk/logs/cdrs',
  zentrunkCDRDetail: (callUuid: string) => `/zentrunk/logs/cdrs/${callUuid}`,
  zentrunkPcap: (callUuid: string) => `/zentrunk/logs/cdrs/${callUuid}/pcap`,
  exportZentrunkLogs: '/zentrunk/logs/cdrs/export',

  // Voice Alerts
  listVoiceAlerts: '/alarms',
  createVoiceAlert: '/alarms',
  updateVoiceAlert: (alarmId: number) => `/alarms/${alarmId}`,
  deleteVoiceAlert: (alarmId: number) => `/alarms/${alarmId}`,
  duplicateVoiceAlert: '/alarms/duplicate',

  // Port-in document auto-fill (SER-6250). Absolute, unlike the entries above: these go
  // through hodor's /v1/ai-assist proxy to aiassist rather than to contacto-core, and
  // baseService adds no version prefix of its own.
  extractPortInFields: '/v1/ai-assist/aiassistant/extract-port-in-fields',
  generatePortInLoa: '/v1/ai-assist/aiassistant/generate-port-in-loa',
} as const

interface ApiConsoleService {
  // Geo Permissions
  listGeoPermissions: GetService<RGeoPermissions, [subaccountAuthId?: string]>
  saveGeoPermission: PostService<void, RGeoPermissionsPayload, [subaccountAuthId?: string]>
  exportGeoPermissions: (subaccountAuthId?: string) => Promise<Blob>

  // SIP Trunking Geo Permissions
  listSipTrunkingGeoPermissions: GetService<RSipTrunkingGeoPermissions, []>
  saveSipTrunkingGeoPermissions: PostService<void, RSipTrunkingGeoPermissionsPayload, []>
  exportSipTrunkingPremiumPrefixes: () => Promise<Blob>
  removeOverride: (subaccountAuthId: string) => Promise<ApiResponse<RGeoPermissions>>

  // SMS Geo Permissions
  listSmsGeoPermissions: GetService<SmsGeoPermissionsResponse, [subaccountAuthId?: string]>
  saveSmsGeoPermissions: PostService<void, SmsGeoPermissionsPayload, [subaccountAuthId?: string]>
  removeSmsGeoPermissionsOverride: (
    subaccountAuthId: string,
    updatedBy: string,
  ) => Promise<ApiResponse<SmsGeoPermissionsResponse>>

  // Call logs
  listCallLogs: (params?: Record<string, string>) => Promise<ApiResponse<RCallLogs>>
  callInsights: (callUuid: string, endDate: string) => Promise<ApiResponse<RCallInsights>>
  audioStats: (callUuid: string, endDate: string) => Promise<ApiResponse<RAudioStats>>
  callDebugLogs: (callUuid: string) => Promise<ApiResponse<RCallDebugLogs>>
  callRawLogs: (callUuid: string) => Promise<ApiResponse<RCallRawLogs>>
  zentrunkPcap: (callUuid: string) => Promise<Blob>

  // Message logs
  listMessageLogs: (params?: Record<string, string>) => Promise<ApiResponse<RMessageLogs>>
  getUsageSummary: (params: {
    from_date: string
    to_date: string
  }) => Promise<ApiResponse<RUsageSummary>>
  // No params: hodor injects auth_id and messaging-dashboard picks the window/currency. Returns the
  // upstream body directly — the analytics proxy streams it verbatim, with no envelope to unwrap.
  getUsageMonthlyTotal: () => Promise<RUsageMonthlyTotal>
  messageInsights: (messageUuid: string) => Promise<ApiResponse<MessageLog>>
  messageDebugLogs: (
    messageUuid: string,
    channel?: 'messaging' | 'whatsapp',
    subaccountAuthId?: string | null,
  ) => Promise<ApiResponse<RMessageDebugLogs>>
  exportMessageLogs: (data: {
    filters: {
      from_date: string
      to_date: string
      message_type: string
    }
  }) => Promise<ApiResponse<Record<string, unknown>>>

  // Zentrunk Logs
  listZentrunkLogs: (params?: Record<string, string>) => Promise<ApiResponse<RZentrunkLogs>>
  zentrunkCDRDetail: (callUuid: string) => Promise<ApiResponse<ZentrunkCDRDetail>>
  exportZentrunkLogs: (data: { filters: Record<string, string> }) => Promise<Blob>

  // Call logs export
  exportCallLogs: (data: {
    filters: {
      from_date: string
      to_date: string
    }
  }) => Promise<ApiResponse<Record<string, unknown>>>

  // Concurrency export
  exportConcurrencyData: (data: {
    filters: {
      from_date: string
      to_date: string
    }
  }) => Promise<ApiResponse<Record<string, unknown>>>

  // Recordings
  listCallRecordings: (
    params?: { callUuid?: string } & Record<string, unknown>,
  ) => Promise<ApiResponse<RCallRecordings>>
  deleteCallRecording: (recordingId: string) => Promise<ApiResponse<unknown>>
  exportRecordings: (data: {
    filters: Record<string, string>
  }) => Promise<ApiResponse<{ email?: string } & Record<string, unknown>>>
  bulkDeleteRecordings: (data: {
    filters: Record<string, string>
  }) => Promise<ApiResponse<Record<string, unknown>>>

  // Transcription
  listCallTranscriptions: (
    params?: { callUuid?: string } & Record<string, unknown>,
  ) => Promise<ApiResponse<RCallTranscriptions>>
  // Voice Alerts
  listVoiceAlerts: (params: Record<string, string>) => Promise<ApiResponse<RVoiceAlerts>>
  createVoiceAlert: PostService<void, RVoiceAlertPayload>
  updateVoiceAlert: (
    alarmId: number,
    data: Partial<RVoiceAlertPayload>,
  ) => Promise<ApiResponse<void>>
  deleteVoiceAlert: (alarmId: number) => Promise<ApiResponse<void>>
  duplicateVoiceAlert: (data: RVoiceAlertPayload) => Promise<ApiResponse<void>>

  // Port-in document auto-fill
  extractPortInFields: (formData: FormData) => Promise<ApiResponse<RPortInExtractionResponse>>
  generatePortInLoa: (payload: PortInLoaRequest) => Promise<ApiResponse<RPortInGeneratedLoa>>
}

const getValidSubaccountId = (subaccountAuthId?: string) =>
  subaccountAuthId && subaccountAuthId !== 'all' ? subaccountAuthId : undefined

// Exported so tests can build a fresh instance after mocking create-service /
// base-services, without depending on this module being imported before any
// other module pulls it in (bun runs all test files in one process).
export const apiServiceFactory = (): ApiConsoleService => {
  const defaultServiceV1 = createService('', true, BackendServiceName.default, 'v1')

  return {
    // Geo Permissions
    listGeoPermissions: (subaccountAuthId?: string) =>
      coreService.get(API_CONSOLE_ENDPOINTS.listGeoPermissions, {
        subaccount_auth_id: getValidSubaccountId(subaccountAuthId),
      }),
    saveGeoPermission: (data: RGeoPermissionsPayload, subaccountAuthId?: string) =>
      coreService.post(API_CONSOLE_ENDPOINTS.saveGeoPermission, data, {
        subaccount_auth_id: getValidSubaccountId(subaccountAuthId),
      }),
    exportGeoPermissions: (subaccountAuthId?: string) =>
      coreService.get<Blob>(API_CONSOLE_ENDPOINTS.exportGeoPermissions, {
        subaccount_auth_id: getValidSubaccountId(subaccountAuthId),
      }),
    removeOverride: (subaccountAuthId: string) =>
      coreService.post(
        API_CONSOLE_ENDPOINTS.removeOverride,
        {},
        {
          subaccount_auth_id: subaccountAuthId,
        },
      ),

    // SIP Trunking Geo Permissions (account-level only — no subaccount param)
    listSipTrunkingGeoPermissions: () =>
      coreService.get(API_CONSOLE_ENDPOINTS.sipTrunkingGeoPermissions),
    saveSipTrunkingGeoPermissions: (data: RSipTrunkingGeoPermissionsPayload) =>
      coreService.post(API_CONSOLE_ENDPOINTS.sipTrunkingGeoPermissions, data),
    exportSipTrunkingPremiumPrefixes: () =>
      coreService.get<Blob>(API_CONSOLE_ENDPOINTS.exportSipTrunkingPremiumPrefixes),

    // SMS Geo Permissions
    listSmsGeoPermissions: (subaccountAuthId?: string) =>
      coreService.get(API_CONSOLE_ENDPOINTS.listSmsGeoPermissions, {
        subaccount_auth_id: getValidSubaccountId(subaccountAuthId),
      }),
    saveSmsGeoPermissions: (data: SmsGeoPermissionsPayload, subaccountAuthId?: string) =>
      coreService.post(API_CONSOLE_ENDPOINTS.saveSmsGeoPermissions, data, {
        subaccount_auth_id: getValidSubaccountId(subaccountAuthId),
      }),
    removeSmsGeoPermissionsOverride: (subaccountAuthId: string, updatedBy: string) =>
      coreService.delete(
        `${API_CONSOLE_ENDPOINTS.removeSmsGeoPermissionsOverride}`,
        { updated_by: updatedBy },
        { params: { subaccount_auth_id: subaccountAuthId } },
      ),

    // Call logs
    listCallLogs: (params) => {
      const convertedParams = { ...params }

      // Normalize phone filters to digits (strips +, spaces, hyphens, parens, etc.) via the
      // shared helper — the upstream Call API exact-matches the full number, so any input
      // format works but partial input won't. Drop the filter when it has no usable digits.
      for (const key of ['from_number', 'to_number'] as const) {
        const sanitized = sanitizePhoneParam(convertedParams[key])
        if (sanitized) convertedParams[key] = sanitized
        else delete convertedParams[key]
      }

      // Convert duration range filter into __gte/__lte params
      // "Any" values (-1 for min, 946080000 for max) mean no filter
      if (convertedParams.bill_duration) {
        const [min, max] = convertedParams.bill_duration.split(',')
        delete convertedParams.bill_duration
        if (min && min !== '-1') convertedParams.bill_duration__gte = min
        if (max && max !== '946080000') convertedParams.bill_duration__lte = max
      }

      // Convert date range from agent timezone to UTC for backend
      if (params?.end_time) {
        const [startDate, endDate] = params.end_time.split(',')
        const agentTimezone = getAgentTimezone()
        delete convertedParams.end_time

        if (startDate)
          convertedParams.end_time__gte = dayjs
            .tz(`${startDate} 00:00:00`, agentTimezone)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss')

        convertedParams.end_time__lte = dayjs
          .tz(`${endDate || startDate} 23:59:59`, agentTimezone)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')
      }

      // Route to contacto-core for subaccount calls, hodor proxy for main account
      if (convertedParams.subaccount && convertedParams.subaccount !== 'main') {
        const subaccountId = convertedParams.subaccount
        delete convertedParams.subaccount
        return coreService.get(
          API_CONSOLE_ENDPOINTS.listSubaccountCallLogs(subaccountId),
          convertedParams,
        )
      }
      delete convertedParams.subaccount
      return defaultServiceV1.get(API_CONSOLE_ENDPOINTS.listCallLogs, convertedParams)
    },
    callInsights: (callUuid: string, endDate: string) =>
      coreService.get(API_CONSOLE_ENDPOINTS.callInsights(callUuid), {
        end_date: endDate,
      }),
    audioStats: (callUuid: string, endDate: string) =>
      coreService.get(API_CONSOLE_ENDPOINTS.audioStats(callUuid), {
        end_date: endDate,
      }),
    callDebugLogs: (callUuid: string) =>
      coreService.get(API_CONSOLE_ENDPOINTS.callDebugLogs(callUuid)),
    callRawLogs: (callUuid: string) => coreService.get(API_CONSOLE_ENDPOINTS.callRawLogs(callUuid)),

    // Call Recordings
    // Used both by the Call Insights page (passes a single callUuid) and the standalone
    // Recordings logs page (passes filters: date range, numbers, subaccount, etc.).
    listCallRecordings: (params = {}) => {
      const { callUuid, ...rest } = params
      const convertedParams: Record<string, unknown> = { ...rest }

      if (callUuid) convertedParams.call_uuid = callUuid

      // Strip non-numeric chars from phone filters (shared with export/bulk-delete).
      for (const key of ['from_number', 'to_number'] as const) {
        const sanitized = sanitizePhoneParam(convertedParams[key])
        if (sanitized) convertedParams[key] = sanitized
        else delete convertedParams[key]
      }

      // Convert duration range filter into __gte/__lte (recording storage duration, in days).
      const durationBounds = durationRangeToBounds(convertedParams.recording_storage_duration)
      delete convertedParams.recording_storage_duration
      const hasDurationFilter = durationBounds.gte !== undefined || durationBounds.lte !== undefined
      if (durationBounds.gte) convertedParams.recording_storage_duration__gte = durationBounds.gte
      if (durationBounds.lte) convertedParams.recording_storage_duration__lte = durationBounds.lte

      // Date range and storage duration are mutually exclusive (duration wins — matches the
      // export/bulk-delete path and the backend), so skip add_time entirely when a duration
      // filter is set. Convert the picked dates from agent timezone to UTC for the API.
      if (typeof convertedParams.add_time === 'string') {
        const addTime = convertedParams.add_time
        delete convertedParams.add_time
        if (!hasDurationFilter) {
          const { gte, lte } = dateRangeToUtc(addTime, getAgentTimezone())
          if (gte) convertedParams.add_time__gte = gte
          if (lte) convertedParams.add_time__lte = lte
        }
      }

      // Route to contacto-core for subaccount calls, hodor proxy for main account
      if (convertedParams.subaccount && convertedParams.subaccount !== 'main') {
        const subaccountId = String(convertedParams.subaccount)
        delete convertedParams.subaccount
        return coreService.get(
          API_CONSOLE_ENDPOINTS.listSubaccountRecordings(subaccountId),
          convertedParams,
        )
      }
      delete convertedParams.subaccount
      return defaultServiceV1.get(API_CONSOLE_ENDPOINTS.listCallRecordings, convertedParams)
    },

    // Delete a single recording via the Plivo API (hodor proxy). Returns 204 on success.
    deleteCallRecording: (recordingId) =>
      defaultServiceV1.delete(API_CONSOLE_ENDPOINTS.deleteRecording(recordingId)),

    // Export recordings (async; emailed to the user) via contacto-core -> Trace.
    exportRecordings: (data) => coreService.post(API_CONSOLE_ENDPOINTS.exportRecordings, data),

    // Bulk-delete recordings matching the filters (async; queued) via contacto-core -> Trace.
    bulkDeleteRecordings: (data) =>
      coreService.delete(API_CONSOLE_ENDPOINTS.bulkDeleteRecordings, data),

    // Call Transcriptions
    // Used both by the Call Insights page (passes a single callUuid) and the standalone
    // Transcriptions logs page (passes filters: date range, call UUID, transcription ID).
    // Param shaping (UTC date bounds, limit cap) lives in buildTranscriptionListParams so it
    // stays a pure, unit-testable function.
    listCallTranscriptions: (params = {}) =>
      defaultServiceV1.get(
        API_CONSOLE_ENDPOINTS.listCallTranscriptions,
        buildTranscriptionListParams(params, getAgentTimezone()),
      ),

    // Message logs
    listMessageLogs: (params) => {
      const convertedParams = { ...params }

      // Convert date range from agent timezone to UTC for backend
      if (params?.message_time) {
        const [startDate, endDate] = params.message_time.split(',')
        const agentTimezone = getAgentTimezone()
        delete convertedParams.message_time

        if (startDate)
          convertedParams.message_time__gte = dayjs
            .tz(`${startDate} 00:00:00`, agentTimezone)
            .utc()
            .format('YYYY-MM-DD HH:mm:ss')

        convertedParams.message_time__lte = dayjs
          .tz(`${endDate || startDate} 23:59:59`, agentTimezone)
          .utc()
          .format('YYYY-MM-DD HH:mm:ss')
      }

      // Drop the redundant end_time range; message_time__gte/lte already covers it.
      // The upstream Plivo Message API rejects (404) when end_time leaks through.
      delete convertedParams.end_time

      // Omit subaccount for the main account; only forward a genuine subaccount auth id.
      // Mirrors listCallLogs so subaccount=main never reaches the Plivo Message API.
      if (!convertedParams.subaccount || convertedParams.subaccount === 'main')
        delete convertedParams.subaccount

      return defaultServiceV1.get(API_CONSOLE_ENDPOINTS.listMessageLogs, convertedParams)
    },
    messageInsights: (messageUuid: string) =>
      coreService.get(API_CONSOLE_ENDPOINTS.messageInsights(messageUuid)),
    messageDebugLogs: (
      messageUuid: string,
      channel: 'messaging' | 'whatsapp' = 'messaging',
      subaccountAuthId?: string | null,
    ) =>
      coreService.get(API_CONSOLE_ENDPOINTS.messageDebugLogs(messageUuid), {
        channel,
        ...(subaccountAuthId ? { subaccount_auth_id: subaccountAuthId } : {}),
      }),
    exportMessageLogs: (data) => coreService.post(API_CONSOLE_ENDPOINTS.exportMessageLogs, data),

    // Zentrunk Logs
    listZentrunkLogs: (params) => {
      const convertedParams = { ...params }

      // Stale duplicate the shared filter persistence can carry over — even
      // without end_time; cdr-api ignores it, so always drop it.
      delete convertedParams.message_time

      // cdr-api exact-matches the stored E.164 number (digits, no '+'), so
      // normalize the searched number to that shape; drop the filter entirely
      // when the input has no usable digits rather than sending an empty one.
      for (const key of ['from_number', 'to_number'] as const) {
        if (!convertedParams[key]) continue
        const normalized = toCdrPhoneNumber(convertedParams[key])
        if (normalized) convertedParams[key] = normalized
        else delete convertedParams[key]
      }

      // Convert the picked date range from the agent timezone to UTC bounds so
      // cdr-api (which treats from_date/to_date as UTC) filters the day the user
      // actually meant. Mirrors listCallLogs/listMessageLogs.
      if (params?.end_time) {
        const { gte, lte } = dateRangeToUtc(params.end_time, getAgentTimezone())
        delete convertedParams.end_time
        if (gte) convertedParams.from_date = gte
        if (lte) convertedParams.to_date = lte
      }

      return coreService.get(API_CONSOLE_ENDPOINTS.listZentrunkLogs, convertedParams)
    },
    zentrunkCDRDetail: (callUuid: string) =>
      coreService.get(API_CONSOLE_ENDPOINTS.zentrunkCDRDetail(callUuid)),
    zentrunkPcap: (callUuid: string) =>
      coreService.get<Blob>(API_CONSOLE_ENDPOINTS.zentrunkPcap(callUuid), undefined, {
        responseType: 'blob',
      }),
    exportZentrunkLogs: (data) =>
      coreService.post(API_CONSOLE_ENDPOINTS.exportZentrunkLogs, data, undefined, {
        responseType: 'blob',
      }),

    // Call logs export
    exportCallLogs: (data) => coreService.post(API_CONSOLE_ENDPOINTS.exportCallLogs, data),

    // Concurrency export
    exportConcurrencyData: (data) =>
      coreService.post(API_CONSOLE_ENDPOINTS.exportConcurrencyData, data),

    // Voice Alerts
    listVoiceAlerts: (params) => coreService.get(API_CONSOLE_ENDPOINTS.listVoiceAlerts, params),
    createVoiceAlert: (data) => coreService.post(API_CONSOLE_ENDPOINTS.createVoiceAlert, data),
    updateVoiceAlert: (alarmId, data) =>
      coreService.post(API_CONSOLE_ENDPOINTS.updateVoiceAlert(alarmId), data),
    deleteVoiceAlert: (alarmId) =>
      coreService.delete(API_CONSOLE_ENDPOINTS.deleteVoiceAlert(alarmId)),
    duplicateVoiceAlert: (data) =>
      coreService.post(API_CONSOLE_ENDPOINTS.duplicateVoiceAlert, data),

    // Usage summary — Plivo UsageSummary API via the hodor plivo-api proxy.
    // granularity is pinned to 'month' last so callers can't override it.
    getUsageSummary: (params) =>
      defaultServiceV1.get(API_CONSOLE_ENDPOINTS.usageSummary, {
        ...params,
        granularity: 'month',
      }),

    // Month-to-date spend via the plivo-analytics proxy. Any query params would be ignored
    // upstream, so none are sent.
    getUsageMonthlyTotal: () => defaultServiceV1.get(API_CONSOLE_ENDPOINTS.usageMonthlyTotal),

    // Port-in document auto-fill (SER-6250): reads an uploaded bill or LOA and returns form
    // fields, each with a status saying how far to trust it.
    extractPortInFields: (formData: FormData) =>
      baseService.post(
        API_CONSOLE_ENDPOINTS.extractPortInFields,
        formData,
        {},
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      ),

    // Fills the real Plivo LOA template from the order the customer just entered, so the
    // letter they sign cannot disagree with what we send their carrier. Returns the .docx
    // base64-encoded rather than as a URL: the letter carries the customer's legal name,
    // service address and every number they own, and the uploader service hands back a
    // permanent unauthenticated link.
    generatePortInLoa: (payload) =>
      baseService.post(API_CONSOLE_ENDPOINTS.generatePortInLoa, payload),
  }
}

export const apiConsoleService = apiServiceFactory()
